import { sanitizePlainText } from "./ContentSanitizer";

export interface FileValidationResult {
  valid: boolean;
  error?: string;
  warnings?: string[];
  sanitizedMetadata?: FileMetadata;
}

export interface FileMetadata {
  originalFilename: string;
  sanitizedFilename: string;
  description?: string;
  tags?: string[];
}

export interface FileValidationConfig {
  maxFileSize?: number;
  acceptedFileTypes?: string[];
  allowedExtensions?: string[];
  enableMagicNumberValidation?: boolean;
  maxFilenameLength?: number;
  sanitizeMetadata?: boolean;
}

export class FileValidation {
  private acceptedFileTypes: string[] = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "image/jpeg",
    "image/png",
  ];

  private allowedExtensions: string[] = [
    ".pdf",
    ".doc",
    ".docx",
    ".jpg",
    ".jpeg",
    ".png",
  ];

  private maxFileSize: number = 1 * 1024 * 1024;
  private maxFilenameLength: number = 255;
  private enableMagicNumberValidation: boolean = true;
  private sanitizeMetadata: boolean = true;

  private readonly magicNumbers: Record<string, string[]> = {
    "application/pdf": ["25504446"],
    "image/jpeg": ["FFD8FF"],
    "image/png": ["89504E47"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
      "504B0304",
    ],
    "application/msword": ["D0CF11E0A1B11AE1"],
  };

  constructor(config?: FileValidationConfig) {
    if (config) {
      this.maxFileSize = config.maxFileSize ?? this.maxFileSize;
      this.acceptedFileTypes =
        config.acceptedFileTypes ?? this.acceptedFileTypes;
      this.allowedExtensions =
        config.allowedExtensions ?? this.allowedExtensions;
      this.enableMagicNumberValidation =
        config.enableMagicNumberValidation ?? this.enableMagicNumberValidation;
      this.maxFilenameLength =
        config.maxFilenameLength ?? this.maxFilenameLength;
      this.sanitizeMetadata = config.sanitizeMetadata ?? this.sanitizeMetadata;
    }
  }

  sanitizeFileMetadata(metadata: Partial<FileMetadata>): FileMetadata {
    const sanitized: FileMetadata = {
      originalFilename: metadata.originalFilename || "",
      sanitizedFilename: this.sanitizeFilenameForXSS(
        metadata.originalFilename || "",
      ),
    };

    if (metadata.description) {
      sanitized.description =
        sanitizePlainText(metadata.description) ?? undefined;
    }

    if (metadata.tags && Array.isArray(metadata.tags)) {
      sanitized.tags = metadata.tags
        .map((tag) => sanitizePlainText(tag))
        .filter(
          (tag): tag is string =>
            tag !== null && tag.length > 0 && tag.length <= 50,
        );
    }

    return sanitized;
  }

  private validateFilename(filename: string): FileValidationResult {
    if (filename.includes("\0") || filename.includes("%00")) {
      return { valid: false, error: "Filename contains null bytes" };
    }
    if (
      filename.includes("..") ||
      filename.includes("/") ||
      filename.includes("\\")
    ) {
      return {
        valid: false,
        error: "Filename contains path traversal characters",
      };
    }
    if (filename.length > this.maxFilenameLength) {
      return {
        valid: false,
        error: `Filename too long (max ${this.maxFilenameLength} characters)`,
      };
    }
    if (filename.startsWith(".")) {
      return { valid: false, error: "Hidden files not allowed" };
    }
    if (filename.startsWith("-") || filename.startsWith(" ")) {
      return {
        valid: false,
        error: "Filename cannot start with hyphen or space",
      };
    }

    const sanitizedFilename = this.sanitizeFilenameForXSS(filename);
    if (sanitizedFilename !== filename) {
      return {
        valid: false,
        error: "Filename contains potentially malicious content",
      };
    }

    const safeFilenamePattern = /^[a-zA-Z0-9._-]+$/;
    if (!safeFilenamePattern.test(filename)) {
      return {
        valid: false,
        error:
          "Filename contains invalid characters. Use only letters, numbers, hyphens, underscores, and periods",
      };
    }

    return { valid: true };
  }

  private sanitizeFilenameForXSS(filename: string): string {
    return sanitizePlainText(filename) ?? filename;
  }

  private validateExtension(filename: string): FileValidationResult {
    const filenameLower = filename.toLowerCase();
    const parts = filenameLower.split(".");

    if (parts.length > 2) {
      return { valid: false, error: "Multiple extensions detected" };
    }

    const extension = parts.length > 1 ? `.${parts[parts.length - 1]}` : "";

    if (!extension) {
      return { valid: false, error: "File must have an extension" };
    }

    if (!this.allowedExtensions.includes(extension)) {
      return {
        valid: false,
        error: `Extension ${extension} not allowed. Allowed: ${this.allowedExtensions.join(", ")}`,
      };
    }

    return { valid: true };
  }

  private async validateFileSignature(
    file: File,
  ): Promise<FileValidationResult> {
    if (!this.enableMagicNumberValidation) return { valid: true };

    try {
      const arrayBuffer = await file.slice(0, 8).arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      const hex = Array.from(bytes)
        .map((b) => b.toString(16).padStart(2, "0").toUpperCase())
        .join("");

      const expectedSignatures = this.magicNumbers[file.type];
      if (!expectedSignatures) {
        return {
          valid: true,
          warnings: ["No signature validation available for this file type"],
        };
      }

      const isValid = expectedSignatures.some((signature) =>
        hex.startsWith(signature),
      );
      if (!isValid) {
        return {
          valid: false,
          error:
            "File signature does not match declared type. Possible spoofing attempt",
        };
      }

      return { valid: true };
    } catch (error) {
      console.error("Error validating file signature:", error);
      return { valid: false, error: "Unable to validate file signature" };
    }
  }

  private validateContentType(file: File): FileValidationResult {
    if (!this.acceptedFileTypes.includes(file.type)) {
      return { valid: false, error: `Content type ${file.type} not allowed` };
    }
    return { valid: true };
  }

  private validateFileSize(file: File): FileValidationResult {
    if (file.size === 0) return { valid: false, error: "File is empty" };
    if (file.size > this.maxFileSize) {
      return {
        valid: false,
        error: `File too large. Maximum: ${this.formatBytes(this.maxFileSize)}`,
      };
    }
    return { valid: true };
  }

  async validateFile(
    file: File,
    metadata?: Partial<FileMetadata>,
  ): Promise<FileValidationResult> {
    const warnings: string[] = [];

    const filenameResult = this.validateFilename(file.name);
    if (!filenameResult.valid) return filenameResult;

    const sizeResult = this.validateFileSize(file);
    if (!sizeResult.valid) return sizeResult;

    const extensionResult = this.validateExtension(file.name);
    if (!extensionResult.valid) return extensionResult;

    const contentTypeResult = this.validateContentType(file);
    if (!contentTypeResult.valid) return contentTypeResult;

    const signatureResult = await this.validateFileSignature(file);
    if (!signatureResult.valid) return signatureResult;
    if (signatureResult.warnings) warnings.push(...signatureResult.warnings);

    let sanitizedMetadata: FileMetadata | undefined;
    if (this.sanitizeMetadata && metadata) {
      sanitizedMetadata = this.sanitizeFileMetadata({
        ...metadata,
        originalFilename: file.name,
      });
    }

    return {
      valid: true,
      warnings: warnings.length > 0 ? warnings : undefined,
      sanitizedMetadata,
    };
  }

  private formatBytes(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  }

  getAcceptedFileTypes(): string[] {
    return this.acceptedFileTypes;
  }
  getMaxFileSize(): number {
    return this.maxFileSize;
  }
  getAllowedExtensions(): string[] {
    return this.allowedExtensions;
  }
  getMaxFilenameLength(): number {
    return this.maxFilenameLength;
  }
}
